import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { useNamespace } from "../../hooks/useNamespace";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import PaymentExpensesSection from "../../components/PaymentExpensesSection";
import { useApi } from "../../hooks/useApi";
import {
  listPaymentInstances,
  paymentInstanceStats,
  getPaymentInstance,
  updatePaymentInstance,
  deletePaymentInstance,
  createReceipt,
  updateReceipt,
  deleteReceipt,
} from "../../api/paymentsService";
import { listAllBusinesses } from "../../api/businessService";
import { listEmployees } from "../../api/employeeService";
import { listTaskMasters } from "../../api/taskMasterService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { fetchAndDownload } from "../../utils/exportCsv";
import { formatDate, formatDateTime, formatINRExact } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { useAuth } from "../../contexts/AuthContext";
import { StatusBadge } from "../../components/Badges";

const STATUS_META = {
  pending: { label: "Pending", tone: "neutral" },
  partial: { label: "Partial", tone: "info" },
  paid: { label: "Paid", tone: "success" },
  overdue: { label: "Overdue", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

const STATUS_ORDER = ["pending", "partial", "overdue", "paid", "cancelled"];

const PATTERN_LABEL = {
  one_off: "One-off",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
};

const GST_LABEL = {
  gst: "GST",
  no_gst: "No GST",
};

// Statuses of the linked one-off task (distinct from the bill's own status).
const TASK_STATUS_LABEL = {
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Where a bill came from. Template bills carry a recurrence pattern; the rest
// are task bills, either machine-raised by the auto-invoice cron or raised by
// hand when a one-off task was attached. `auto_invoice_generated_at` is the
// discriminator — both kinds are master-less with a task attached, so
// period_label ('oneoff' vs 'YYYY-MM') would be an unsafe signal.
function sourceLabel(row) {
  if (!row) return "";
  if (row.master) return PATTERN_LABEL[row.master.recurrence_pattern] || "";
  if (row.employee_task?.auto_invoice_generated_at) return "Auto-invoice";
  return "One-off";
}

// The first/original assignee of a linked task: the earliest assignment's
// `from` (or its `to` when first assigned from nobody), falling back to the
// current assignee when the task was never transferred.
function firstAssigneeName(task) {
  if (!task) return null;
  const fa = task.first_assignment;
  if (fa) return fa.from_employee?.name ?? fa.to_employee?.name ?? task.employee?.name ?? null;
  return task.employee?.name ?? null;
}

export default function PaymentsOversightPage() {
  const [filters, setFilters] = useState({
    business_id: "",
    status: ["pending", "partial", "overdue"], // exclude paid/cancelled by default
    from: "",
    to: "",
    q: "",
    gst: "", // "" = any, "gst", "no_gst"
    task_status: "", // linked-task status; only used on the Task payments tab
    first_assignee_id: "", // linked-task's original assignee; Task tab only
    assignee_id: "", // linked-task assignee; only used on the Task payments tab
    reviewer_id: "", // linked-task reviewer; only used on the Task payments tab
    task_master_id: "", // linked-task's template; Task payments tab only
    has_invoice: "", // "" = any, "yes" = invoice attached, "no" = none
  });
  const [debouncedQ, setDebouncedQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("all"); // "all" | "task"
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const navigate = useNavigate();
  const ns = useNamespace();
  const { hasPermission, role } = useAuth();
  // Read-only employees (payments.view without payments.edit) see the page
  // but no edit affordances. Admins are super-users → always true.
  const canEdit = hasPermission("payments.edit");
  // Hard delete is destructive recovery — admin-only on the server, so only
  // admins get the affordance (employees with payments.edit can still cancel).
  const canDelete = role === "admin";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 250);
    return () => clearTimeout(t);
  }, [filters.q]);

  const stats = useApi(() => paymentInstanceStats(), []);
  const statsData = stats.data?.data || {};

  const businesses = useApi(() => listAllBusinesses({ per_page: 200 }), []);
  const businessRows = paginatedRows(businesses.data);
  const businessOptions = useMemo(
    () => [
      { id: "", label: "All businesses" },
      ...businessRows.map((b) => ({ id: String(b.id), label: b.business_name })),
    ],
    [businessRows],
  );

  // Employees power the assignee/reviewer filters on the Task payments tab.
  const employees = useApi(() => listEmployees({ per_page: 200 }), []);
  const employeeRows = paginatedRows(employees.data);
  const employeeOptions = useMemo(
    () => [
      { id: "", label: "All" },
      ...employeeRows.map((e) => ({ id: String(e.id), label: e.name })),
    ],
    [employeeRows],
  );

  // Task templates power the task-master filter on the Task payments tab.
  const taskMasters = useApi(() => listTaskMasters({ per_page: 500 }), []);
  const taskMasterRows = paginatedRows(taskMasters.data);
  const taskMasterOptions = useMemo(
    () => [
      { id: "", label: "All task masters" },
      ...taskMasterRows.map((t) => ({
        id: String(t.id),
        label: t.name,
        hint: t.task_group?.name,
      })),
    ],
    [taskMasterRows],
  );

  const queryParams = useMemo(() => {
    const p = { page, per_page: perPage };
    if (filters.business_id) p.business_id = filters.business_id;
    if (filters.status?.length) p["status"] = filters.status;
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    if (filters.gst) p.gst = filters.gst;
    if (filters.has_invoice) p.has_invoice = filters.has_invoice;
    if (tab === "task") {
      p.source = "task";
      if (filters.task_status) p.task_status = filters.task_status;
      if (filters.first_assignee_id) p.first_assignee_id = filters.first_assignee_id;
      if (filters.assignee_id) p.assignee_id = filters.assignee_id;
      if (filters.reviewer_id) p.reviewer_id = filters.reviewer_id;
      if (filters.task_master_id) p.task_master_id = filters.task_master_id;
    }
    if (debouncedQ) p.q = debouncedQ;
    return p;
  }, [page, perPage, filters.business_id, filters.status, filters.from, filters.to, filters.gst, filters.has_invoice, filters.task_status, filters.first_assignee_id, filters.assignee_id, filters.reviewer_id, filters.task_master_id, tab, debouncedQ]);

  // Reset to the first page whenever the filters/tab/page-size change so we
  // never land on an out-of-range page after the result set shrinks.
  useEffect(() => {
    setPage(1);
  }, [perPage, filters.business_id, filters.status, filters.from, filters.to, filters.gst, filters.has_invoice, filters.task_status, filters.first_assignee_id, filters.assignee_id, filters.reviewer_id, filters.task_master_id, tab, debouncedQ]);

  const instances = useApi(() => listPaymentInstances(queryParams), [JSON.stringify(queryParams)]);
  const rows = paginatedRows(instances.data);
  const meta = paginatedMeta(instances.data);

  const toggleStatus = (s) => {
    setFilters((f) => ({
      ...f,
      status: f.status.includes(s) ? f.status.filter((x) => x !== s) : [...f.status, s],
    }));
  };

  const refreshAll = () => {
    stats.reload();
    instances.reload();
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      // Strip pagination — export streams the full filtered set.
      const { per_page: _ignore, ...params } = queryParams;
      await fetchAndDownload("/payment-instances/export", params, "payments_oversight");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Payments Oversight</h1>
          <div className="page-sub">Outstanding bills, overdue follow-ups, and collection KPIs</div>
        </div>
        <div className="page-actions">
          {/* Export — admin-only. Employees with payments.view still see the
              table in-app but can't pull the full filtered set as a file. */}
          {role === "admin" && (
            <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting}>
              <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )}
          <button className="btn btn-secondary" onClick={refreshAll}>
            <Icon name="refresh-cw" size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* KPIs — admin-only. Employees with payments.view see the bills table
          but not the firm-wide collection totals. */}
      {role === "admin" && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <KpiCard
            label="Outstanding"
            value={formatINRExact(statsData.outstanding_total)}
            tone="warning"
            icon="dollar-sign"
            hint="All unpaid amounts across non-cancelled bills"
          />
          <KpiCard
            label="Overdue"
            value={formatINRExact(statsData.overdue_amount)}
            subValue={statsData.overdue_count != null ? `${statsData.overdue_count} bill${statsData.overdue_count === 1 ? "" : "s"}` : null}
            tone="danger"
            icon="alert-triangle"
            hint="Past due, still unpaid"
          />
          <KpiCard
            label="Due this month"
            value={formatINRExact(statsData.due_this_month_amount)}
            tone="info"
            icon="calendar"
            hint="Bills with a due date in the current calendar month"
          />
          <KpiCard
            label="Collected this month"
            value={formatINRExact(statsData.collected_this_month)}
            tone="success"
            icon="check-circle"
            hint="Sum of paid bills closed this month"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        {[
          { id: "all", label: "All bills" },
          { id: "task", label: "Task payments" },
        ].map((t) => (
          <button
            key={t.id}
            className={`chip ${tab === t.id ? "active" : ""}`}
            onClick={() => {
              setTab(t.id);
              // The task-only filters (status/assignee/reviewer) apply to task
              // bills — drop them when moving off the Task payments tab.
              if (t.id !== "task") setFilters((f) => ({ ...f, task_status: "", first_assignee_id: "", assignee_id: "", reviewer_id: "", task_master_id: "" }));
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body col gap-md">
          <div className="row gap-md" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 2, minWidth: 220 }}>
              <label className="field-label">Business</label>
              <SearchableSelect
                value={filters.business_id}
                onChange={(v) => setFilters({ ...filters, business_id: v ?? "" })}
                options={businessOptions}
                placeholder="All businesses"
                searchPlaceholder="Search businesses…"
                width="100%"
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140 }}>
              <label className="field-label">Due from</label>
              <input
                type="date"
                className="input"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140 }}>
              <label className="field-label">Due to</label>
              <input
                type="date"
                className="input"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 130 }}>
              <label className="field-label">GST</label>
              <select
                className="select"
                value={filters.gst}
                onChange={(e) => setFilters({ ...filters, gst: e.target.value })}
              >
                <option value="">Any</option>
                <option value="gst">GST</option>
                <option value="no_gst">No GST</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 150 }}>
              <label className="field-label">Invoice</label>
              <select
                className="select"
                value={filters.has_invoice}
                onChange={(e) => setFilters({ ...filters, has_invoice: e.target.value })}
              >
                <option value="">Any</option>
                <option value="yes">Invoice attached</option>
                <option value="no">No invoice</option>
              </select>
            </div>
            {tab === "task" && (
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Task master</label>
                <SearchableSelect
                  value={filters.task_master_id}
                  onChange={(v) => setFilters({ ...filters, task_master_id: v ?? "" })}
                  options={taskMasterOptions}
                  placeholder="All task masters"
                  searchPlaceholder="Search task masters…"
                  width="100%"
                />
              </div>
            )}
            {tab === "task" && (
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label className="field-label">Task status</label>
                <select
                  className="select"
                  value={filters.task_status}
                  onChange={(e) => setFilters({ ...filters, task_status: e.target.value })}
                >
                  <option value="">Any status</option>
                  {Object.entries(TASK_STATUS_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            )}
            {tab === "task" && (
              <div className="field" style={{ flex: 1, minWidth: 160 }}>
                <label className="field-label">First assignee</label>
                <SearchableSelect
                  value={filters.first_assignee_id}
                  onChange={(v) => setFilters({ ...filters, first_assignee_id: v ?? "" })}
                  options={employeeOptions}
                  placeholder="All"
                  searchPlaceholder="Search employees…"
                  width="100%"
                />
              </div>
            )}
            {tab === "task" && (
              <div className="field" style={{ flex: 1, minWidth: 160 }}>
                <label className="field-label">Assignee</label>
                <SearchableSelect
                  value={filters.assignee_id}
                  onChange={(v) => setFilters({ ...filters, assignee_id: v ?? "" })}
                  options={employeeOptions}
                  placeholder="All"
                  searchPlaceholder="Search employees…"
                  width="100%"
                />
              </div>
            )}
            {tab === "task" && (
              <div className="field" style={{ flex: 1, minWidth: 160 }}>
                <label className="field-label">Reviewer</label>
                <SearchableSelect
                  value={filters.reviewer_id}
                  onChange={(v) => setFilters({ ...filters, reviewer_id: v ?? "" })}
                  options={employeeOptions}
                  placeholder="All"
                  searchPlaceholder="Search employees…"
                  width="100%"
                />
              </div>
            )}
            <div className="field" style={{ flex: 2, minWidth: 200 }}>
              <label className="field-label">Search</label>
              <input
                type="text"
                className="input"
                placeholder="Business, template or task name…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </div>
          </div>

          <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
            <span className="t-muted text-xs" style={{ alignSelf: "center", marginRight: 6 }}>Status:</span>
            {STATUS_ORDER.map((s) => {
              const active = filters.status.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  className={`btn btn-sm ${active ? `badge-${STATUS_META[s].tone === "neutral" ? "" : STATUS_META[s].tone}` : "btn-secondary"}`}
                  style={active ? toneStyles(STATUS_META[s].tone) : null}
                  onClick={() => toggleStatus(s)}
                >
                  {active && <Icon name="check" size={11} style={{ marginRight: 4 }} />}
                  {STATUS_META[s].label}
                </button>
              );
            })}
            {(filters.business_id || filters.from || filters.to || filters.q || filters.gst || filters.has_invoice || filters.task_status || filters.first_assignee_id || filters.assignee_id || filters.reviewer_id || filters.task_master_id || filters.status.length !== STATUS_ORDER.length) && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: "auto" }}
                onClick={() => setFilters({ business_id: "", status: STATUS_ORDER, from: "", to: "", q: "", gst: "", has_invoice: "", task_status: "", first_assignee_id: "", assignee_id: "", reviewer_id: "", task_master_id: "" })}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body no-pad">
          {instances.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="dollar-sign"
              title="No payment instances"
              hint="Once a template is attached to a business and the generator runs, bills will appear here."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Template</th>
                  {tab === "task" && <th>First assignee</th>}
                  {tab === "task" && <th>Assignee</th>}
                  {tab === "task" && <th>Reviewer</th>}
                  <th>GST</th>
                  <th>Period</th>
                  <th className="t-num">Due</th>
                  <th className="t-num">Expected</th>
                  <th className="t-num">Paid</th>
                  <th className="t-num">Outstanding</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  return (
                    <tr key={r.id} className="clickable" onClick={() => setOpenId(r.id)}>
                      <td className="t-primary fw-500">{r.business?.business_name || "—"}</td>
                      <td>
                        <div className="row gap-sm" style={{ alignItems: "center" }}>
                          <span>{r.master?.name || r.title || "—"}</span>
                          {r.employee_task_id && <span className="badge badge-info">Task</span>}
                        </div>
                        <div className="text-xs t-muted">{sourceLabel(r)}</div>
                      </td>
                      {tab === "task" && (
                        <td className="t-muted text-xs">{firstAssigneeName(r.employee_task) || "—"}</td>
                      )}
                      {tab === "task" && (
                        <td className="t-muted text-xs">{r.employee_task?.employee?.name || "—"}</td>
                      )}
                      {tab === "task" && (
                        <td className="t-muted text-xs">{r.employee_task?.reviewer?.name || "—"}</td>
                      )}
                      <td>
                        {(() => {
                          const gst = r.gst_treatment ?? r.master?.gst_treatment;
                          return (
                            <span className={`badge ${gst === "gst" ? "badge-info" : "badge-neutral"}`}>
                              {GST_LABEL[gst] || "No GST"}
                            </span>
                          );
                        })()}
                      </td>
                      <td><span className="badge badge-neutral">{r.period_label}</span></td>
                      <td className="t-num t-muted">{formatDate(r.due_date)}</td>
                      <td className="t-num">{formatINRExact(r.expected_amount)}</td>
                      <td className="t-num t-muted">{formatINRExact(r.amount_paid)}</td>
                      <td className="t-num fw-600">{formatINRExact(r.amount_outstanding)}</td>
                      <td>
                        <span className="badge" style={toneStyles(meta.tone)}>{meta.label}</span>
                      </td>
                      <td>
                        {/* Invoice attached badge — links to the latest one
                            if present. Clicking it must not also trigger the
                            row's open-drawer handler. */}
                        {r.invoices?.length > 0 ? (
                          <span
                            className="badge badge-success"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); navigate(`${ns}/invoices/${r.invoices[0].id}`); }}
                            title={`View invoice #${r.invoices[0].invoice_no}`}
                          >
                            <Icon name="file-text" size={10} /> #{r.invoices[0].invoice_no}
                          </span>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <Pagination
          meta={meta}
          page={page}
          onPageChange={setPage}
          perPage={perPage}
          onPerPageChange={setPerPage}
          disabled={instances.loading}
        />
      </div>

      <PaymentInstanceDrawer
        instanceId={openId}
        canEdit={canEdit}
        canDelete={canDelete}
        canInvoice={hasPermission("payments.edit")}
        onClose={() => setOpenId(null)}
        onChanged={refreshAll}
        onDeleted={() => { setOpenId(null); refreshAll(); }}
      />
    </>
  );
}

function KpiCard({ label, value, subValue, tone = "neutral", icon, hint }) {
  return (
    <div className="card">
      <div className="card-body row gap-sm" style={{ alignItems: "flex-start" }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...iconBg(tone),
          }}
        >
          <Icon name={icon} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5, marginTop: 2, ...toneText(tone) }}>{value}</div>
          {subValue && <div className="text-xs t-muted" style={{ marginTop: 2 }}>{subValue}</div>}
          {hint && <div className="text-xs t-muted" style={{ marginTop: 6 }}>{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function toneStyles(tone) {
  switch (tone) {
    case "success": return { background: "var(--success-100)", color: "var(--success)", borderColor: "transparent" };
    case "warning": return { background: "var(--warning-100)", color: "var(--warning)", borderColor: "transparent" };
    case "danger":  return { background: "#fee2e2", color: "var(--danger)", borderColor: "transparent" };
    case "info":    return { background: "var(--info-100)", color: "var(--info)", borderColor: "transparent" };
    default:        return { background: "var(--surface-2)", color: "var(--ink-700)", borderColor: "transparent" };
  }
}
function iconBg(tone) {
  switch (tone) {
    case "success": return { background: "var(--success-100)", color: "var(--success)" };
    case "warning": return { background: "var(--warning-100)", color: "var(--warning)" };
    case "danger":  return { background: "#fee2e2", color: "var(--danger)" };
    case "info":    return { background: "var(--info-100)", color: "var(--info)" };
    default:        return { background: "var(--primary-50)", color: "var(--primary-700)" };
  }
}
function toneText(tone) {
  switch (tone) {
    case "danger":  return { color: "var(--danger)" };
    case "warning": return { color: "var(--warning)" };
    case "success": return { color: "var(--success)" };
    default:        return { color: "var(--ink-900)" };
  }
}

function PaymentInstanceDrawer({ instanceId, canEdit = false, canDelete = false, canInvoice = false, onClose, onChanged, onDeleted }) {
  const open = !!instanceId;
  const navigate = useNavigate();
  const ns = useNamespace();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ expected_amount: "", due_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) { setInstance(null); return; }
    let alive = true;
    setLoading(true);
    getPaymentInstance(instanceId)
      .then((res) => {
        if (!alive) return;
        const d = res?.data || null;
        setInstance(d);
        if (d) {
          setForm({
            expected_amount: d.expected_amount ?? "",
            due_date: d.due_date ? String(d.due_date).slice(0, 10) : "",
            notes: d.notes ?? "",
          });
        }
      })
      .catch((err) => alive && showApiError(err))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, instanceId]);

  const save = async (extra = {}) => {
    if (!instance) return;
    setSaving(true);
    try {
      const payload = {
        expected_amount: form.expected_amount === "" ? undefined : Number(form.expected_amount),
        due_date: form.due_date || undefined,
        notes: form.notes,
        ...extra,
      };
      const res = await updatePaymentInstance(instance.id, payload);
      setInstance(res.data);
      showToast("success", "Saved");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!instance) return;
    if (!window.confirm("Cancel this bill? No further receipts can be posted until you un-cancel.")) return;
    setCancelling(true);
    try {
      await save({ status: "cancelled" });
    } finally {
      setCancelling(false);
    }
  };

  const uncancel = async () => {
    if (!instance) return;
    setCancelling(true);
    try {
      await save({ status: "pending" });
    } finally {
      setCancelling(false);
    }
  };

  const remove = async () => {
    if (!instance) return;
    const receiptCount = Array.isArray(instance.receipts) ? instance.receipts.length : 0;
    const warn = receiptCount > 0
      ? ` This also permanently deletes ${receiptCount} recorded receipt${receiptCount === 1 ? "" : "s"}.`
      : "";
    if (!window.confirm(`Delete this bill permanently? This cannot be undone.${warn}\n\nTo keep history, use "Cancel bill" instead.`)) return;
    setDeleting(true);
    try {
      await deletePaymentInstance(instance.id);
      showToast("success", "Bill deleted");
      onDeleted?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setDeleting(false);
    }
  };

  const meta = instance ? STATUS_META[instance.status] : null;

  // Header parts joined from an array rather than a template literal: a
  // master-less bill has no `master.name`, and interpolating that undefined
  // printed the literal text "undefined" after the client name.
  //
  // The fallback is `title`, not employee_task.task_master.name — both hold the
  // task name, but the PATCH response only reloads business + master, so the
  // task relation is gone from state after a save while `title` (a plain
  // column) survives.
  const drawerTitle = instance
    ? [instance.business?.business_name, instance.master?.name || instance.title]
        .filter(Boolean).join(" · ") || "Bill"
    : "Bill";
  const drawerSubtitle = instance
    ? [sourceLabel(instance), instance.period_label].filter(Boolean).join(" · ") || null
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      subtitle={drawerSubtitle}
      footer={
        <>
          {canDelete && instance && (
            <button
              className="btn btn-sm"
              style={{ marginRight: "auto", color: "var(--danger)", borderColor: "var(--danger)" }}
              onClick={remove}
              disabled={deleting}
              title="Permanently delete this bill (and its receipts)"
            >
              <Icon name="trash" size={12} /> {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canInvoice && instance && instance.status !== "cancelled" && (
            // If an invoice already exists for this bill, point at the latest
            // one's print preview instead of opening the generate form — same
            // button slot, different verb.
            instance.invoices?.length > 0 ? (
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`${ns}/invoices/${instance.invoices[0].id}`)}
                title={`View invoice #${instance.invoices[0].invoice_no}`}
              >
                <Icon name="file-text" size={13} /> View invoice
              </button>
            ) : (
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`${ns}/invoices/new?payment_instance_id=${instance.id}`)}
                title="Open the invoice form prefilled from this bill"
              >
                <Icon name="file-text" size={13} /> Generate invoice
              </button>
            )
          )}
          {canEdit && instance && instance.status === "cancelled" ? (
            <button className="btn btn-primary" onClick={uncancel} disabled={cancelling}>
              {cancelling ? "Saving…" : "Un-cancel"}
            </button>
          ) : canEdit && instance ? (
            <button
              className="btn btn-sm"
              style={{ background: "var(--danger)", color: "white" }}
              onClick={cancel}
              disabled={cancelling}
            >
              <Icon name="x" size={12} /> {cancelling ? "…" : "Cancel bill"}
            </button>
          ) : null}
          {canEdit && (
            <button className="btn btn-primary" onClick={() => save()} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          )}
        </>
      }
    >
      {loading || !instance ? (
        <LoadingBlock rows={3} />
      ) : (
        <div className="col gap-md">
          <div className="row gap-md" style={{ flexWrap: "wrap" }}>
            <Stat label="Expected" value={formatINRExact(instance.expected_amount)} />
            <Stat label="Paid" value={formatINRExact(instance.amount_paid)} />
            <Stat label="Outstanding" value={formatINRExact(instance.amount_outstanding)} highlight={instance.amount_outstanding > 0 ? "danger" : "success"} />
            <div>
              <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>Status</div>
              <span className="badge" style={{ ...toneStyles(meta?.tone || "neutral"), marginTop: 4 }}>{meta?.label}</span>
            </div>
          </div>

          {instance.employee_task && (
            <div className="card" style={{ background: "var(--surface-2)" }}>
              <div className="card-body col gap-sm">
                <div className="t-primary fw-600 text-sm">Linked task</div>
                <div className="row gap-md" style={{ flexWrap: "wrap" }}>
                  <div style={{ minWidth: 110 }}>
                    <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>Status</div>
                    <div style={{ marginTop: 4 }}><StatusBadge status={instance.employee_task.status} /></div>
                  </div>
                  <div style={{ minWidth: 110 }}>
                    <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>First assignee</div>
                    <div className="text-sm" style={{ marginTop: 6 }}>{firstAssigneeName(instance.employee_task) || "—"}</div>
                  </div>
                  <div style={{ minWidth: 110 }}>
                    <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>Assignee</div>
                    <div className="text-sm" style={{ marginTop: 6 }}>{instance.employee_task.employee?.name || "—"}</div>
                  </div>
                  <div style={{ minWidth: 110 }}>
                    <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>Reviewer</div>
                    <div className="text-sm" style={{ marginTop: 6 }}>{instance.employee_task.reviewer?.name || "—"}</div>
                  </div>
                </div>

                {Array.isArray(instance.employee_task.assignments) && instance.employee_task.assignments.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 6 }}>
                      Transfer history
                    </div>
                    <div className="col gap-sm">
                      {instance.employee_task.assignments.map((a) => {
                        const from = a.from_employee?.name;
                        const to = a.to_employee?.name;
                        const by = a.changed_by?.name;
                        const label = !from && to ? `Assigned to ${to}`
                          : from && !to ? `Unassigned from ${from}`
                          : from && to ? `${from} → ${to}`
                          : "Reassigned";
                        return (
                          <div key={a.id} className="text-sm">
                            <span className="t-primary">{label}</span>
                            <span className="t-muted text-xs">
                              {" · "}{formatDateTime(a.created_at)}{by ? ` · by ${by}` : ""}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <PaymentExpensesSection
            instance={instance}
            canManage={canEdit}
            onChanged={async () => {
              try {
                const res = await getPaymentInstance(instance.id);
                const d = res?.data || null;
                setInstance(d);
                if (d) setForm((f) => ({ ...f, expected_amount: d.expected_amount ?? "" }));
                onChanged?.();
              } catch (err) {
                showApiError(err);
              }
            }}
          />

          <div className="field">
            <label className="field-label">Total expected (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              value={form.expected_amount}
              onChange={(e) => setForm({ ...form, expected_amount: e.target.value })}
              readOnly={!canEdit}
            />
            <div className="field-hint">The full amount owed, including expenses. Editing here only changes this single bill; future bills still use the template default + override.</div>
          </div>

          <div className="field">
            <label className="field-label">Due date</label>
            <input
              type="date"
              className="input"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              readOnly={!canEdit}
            />
          </div>

          <div className="field">
            <label className="field-label">Notes <span className="t-muted text-xs">(admin-only)</span></label>
            <textarea
              className="textarea"
              rows={3}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes about this bill"
              readOnly={!canEdit}
            />
          </div>

          <ReceiptsSection
            instance={instance}
            canEdit={canEdit}
            onChanged={async () => {
              try {
                const res = await getPaymentInstance(instance.id);
                setInstance(res?.data || null);
                onChanged?.();
              } catch (err) {
                showApiError(err);
              }
            }}
          />
        </div>
      )}
    </Drawer>
  );
}

function ReceiptsSection({ instance, canEdit = false, onChanged }) {
  const receipts = Array.isArray(instance.receipts) ? instance.receipts : [];
  const [editing, setEditing] = useState(null); // null | 'new' | <receipt id>

  const outstanding = Number(instance.amount_outstanding ?? 0);
  const isCancelled = instance.status === "cancelled";

  const remove = async (receipt) => {
    if (!window.confirm(`Delete the ₹${Number(receipt.amount).toFixed(2)} receipt from ${formatDate(receipt.received_on)}?`)) return;
    try {
      await deleteReceipt(receipt.id);
      showToast("success", "Receipt deleted");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="col gap-sm" style={{ marginTop: 4 }}>
      <div className="row" style={{ alignItems: "center" }}>
        <div className="t-primary fw-600">Receipts</div>
        <span className="t-muted text-xs" style={{ marginLeft: 8 }}>
          {receipts.length} record{receipts.length === 1 ? "" : "s"}
        </span>
        <div style={{ flex: 1 }} />
        {canEdit && !isCancelled && editing !== "new" && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing("new")}>
            <Icon name="plus" size={12} /> Record receipt
          </button>
        )}
      </div>

      {isCancelled && (
        <div className="text-xs" style={{ color: "var(--warning)" }}>
          This bill is cancelled. Un-cancel it before recording a receipt.
        </div>
      )}

      {editing === "new" && (
        <ReceiptForm
          mode="create"
          instanceId={instance.id}
          defaultAmount={outstanding > 0 ? outstanding : undefined}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged?.(); }}
        />
      )}

      {receipts.length === 0 && editing !== "new" ? (
        <div className="text-xs t-muted" style={{ padding: "8px 0" }}>No receipts yet.</div>
      ) : (
        <div className="col gap-sm">
          {receipts.map((r) => editing === r.id ? (
            <ReceiptForm
              key={r.id}
              mode="edit"
              receipt={r}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); onChanged?.(); }}
            />
          ) : (
            <ReceiptRow
              key={r.id}
              receipt={r}
              canEdit={canEdit}
              onEdit={() => setEditing(r.id)}
              onDelete={() => remove(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const METHOD_LABEL = {
  cash: "Cash",
  bank_transfer: "Bank transfer",
  upi: "UPI",
  cheque: "Cheque",
  card: "Card",
  other: "Other",
};

function ReceiptRow({ receipt, canEdit = false, onEdit, onDelete }) {
  return (
    <div
      className="row"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "8px 12px",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row gap-sm" style={{ alignItems: "baseline" }}>
          <span className="t-primary fw-600">{formatINRExact(receipt.amount)}</span>
          <span className="badge badge-neutral">{METHOD_LABEL[receipt.method] || receipt.method}</span>
          <span className="t-muted text-xs">{formatDate(receipt.received_on)}</span>
        </div>
        {(receipt.reference || receipt.notes) && (
          <div className="text-xs t-muted" style={{ marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {receipt.reference && <>ref: <span className="t-mono">{receipt.reference}</span></>}
            {receipt.reference && receipt.notes && " · "}
            {receipt.notes}
          </div>
        )}
        {receipt.recorder?.name && (
          <div className="text-xs t-muted" style={{ marginTop: 2 }}>
            by {receipt.recorder.name}
          </div>
        )}
      </div>
      {canEdit && (
        <>
          <button className="icon-btn" title="Edit" onClick={onEdit}><Icon name="edit" size={13} /></button>
          <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={onDelete}><Icon name="trash" size={13} /></button>
        </>
      )}
    </div>
  );
}

function ReceiptForm({ mode, instanceId, receipt, defaultAmount, onCancel, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    amount: receipt?.amount ?? (defaultAmount ?? ""),
    received_on: receipt?.received_on ? String(receipt.received_on).slice(0, 10) : today,
    method: receipt?.method ?? "bank_transfer",
    reference: receipt?.reference ?? "",
    notes: receipt?.notes ?? "",
  }));
  const [saving, setSaving] = useState(false);

  const futureDated = form.received_on && form.received_on > today;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount),
        received_on: form.received_on,
        method: form.method,
        reference: form.reference || null,
        notes: form.notes || null,
      };
      if (mode === "edit") {
        await updateReceipt(receipt.id, payload);
        showToast("success", "Receipt updated");
      } else {
        await createReceipt(instanceId, payload);
        showToast("success", "Receipt recorded");
      }
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="card"
      style={{ background: "var(--primary-50)", borderColor: "#bfdbfe" }}
    >
      <div className="card-body col gap-sm">
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className="input"
              autoFocus
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Received on</label>
            <input
              type="date"
              className="input"
              value={form.received_on}
              onChange={(e) => setForm({ ...form, received_on: e.target.value })}
            />
            {futureDated && (
              <div className="field-hint" style={{ color: "var(--warning)" }}>
                Post-dated — fine for cheques, otherwise double-check.
              </div>
            )}
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Method</label>
            <select
              className="select"
              value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value })}
            >
              {Object.entries(METHOD_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label className="field-label">Reference <span className="t-muted text-xs">(optional)</span></label>
          <input
            type="text"
            className="input"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder="Txn id · cheque number · UPI ref"
          />
        </div>

        <div className="field">
          <label className="field-label">Notes <span className="t-muted text-xs">(optional)</span></label>
          <textarea
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={saving || !form.amount || Number(form.amount) <= 0}
          >
            <Icon name="check" size={12} /> {saving ? "Saving…" : mode === "edit" ? "Save" : "Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const color = highlight === "danger" ? "var(--danger)"
    : highlight === "success" ? "var(--success)"
    : "var(--ink-900)";
  return (
    <div style={{ minWidth: 110 }}>
      <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, color }}>{value}</div>
    </div>
  );
}
